import React from 'react'
import ReactDOM from 'react-dom/client'
import App from './App.tsx'
import './index.css'
import { initClarity, initSentry } from './lib/observability'

// Initialize Microsoft Clarity once, before render, so early events are buffered.
// No-op unless enabled for this environment (see src/lib/observability/clarity.ts).
initClarity()

// Initialize Sentry error monitoring + tracing + replay.
// No-op unless VITE_SENTRY_DSN is set and the environment is enabled.
initSentry()

type GlobalResetWindow = Window & {
  resetApp?: () => void
  forceReset?: () => void
}

const globalWin = window as GlobalResetWindow

const fallbackReset = (replace: boolean) => {
  console.log(`[Main] ${replace ? 'forceReset' : 'resetApp'} fallback invoked`)
  localStorage.clear()
  sessionStorage.clear()
  if (replace) {
    window.location.replace('/login')
  } else {
    window.location.href = '/login'
  }
}

if (typeof globalWin.resetApp !== 'function') {
  globalWin.resetApp = () => fallbackReset(false)
}

if (typeof globalWin.forceReset !== 'function') {
  globalWin.forceReset = () => fallbackReset(true)
}

// Apply saved theme BEFORE render to prevent flash
const savedTheme = localStorage.getItem('bizcore-theme')
let theme = 'light' // default

if (savedTheme) {
  try {
    const parsed = JSON.parse(savedTheme)
    theme = parsed?.state?.theme || 'light'
  } catch {
    theme = 'light'
  }
}

if (theme === 'dark') {
  document.documentElement.classList.add('dark')
  document.documentElement.classList.remove('light')
} else {
  document.documentElement.classList.add('light')
  document.documentElement.classList.remove('dark')
}

const USE_MOCK = import.meta.env.VITE_USE_MOCK === 'true'

// Mock mode must not run on top of the app's real-backend service worker (public/sw.js).
// A visitor who previously loaded a non-mock build keeps that SW registered — it caches
// /api/* GET responses (even cross-origin) and would serve stale real-backend data on this
// origin, defeating MSW. MSW's own worker isn't registered yet at this point, so unregister
// any leftover app SW (identified by its /sw.js script URL — never MSW's mockServiceWorker.js)
// and drop its caches before starting MSW. Best-effort: cleanup must never block boot.
async function purgeAppServiceWorker() {
  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations()
      await Promise.all(
        regs
          .filter((r) => {
            const sw = r.active || r.waiting || r.installing
            return sw ? sw.scriptURL.endsWith('/sw.js') : false
          })
          .map((r) => r.unregister())
      )
    }
    if ('caches' in window) {
      const keys = await caches.keys()
      await Promise.all(keys.filter((k) => k.startsWith('bizcore')).map((k) => caches.delete(k)))
    }
  } catch {
    // best-effort — a cleanup failure must not stop the app from rendering
  }
}

async function bootstrap() {
  // Mock mode (VITE_USE_MOCK=true): start MSW and seed a demo session BEFORE
  // render, so the app boots standalone with no backend. Dynamically imported so
  // MSW is never included in a normal production bundle.
  if (USE_MOCK) {
    // Evict any stale app service worker + its cached /api data first, so MSW starts clean.
    await purgeAppServiceWorker()
    // Start MSW only — do NOT seed an authenticated session. The demo starts
    // unauthenticated on the landing page; the user goes through the real flow
    // (Landing → Sign in → Dashboard), which the mock /auth/login backs.
    const { enableMocking } = await import('./mocks/browser')
    await enableMocking()
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <App />
    </React.StrictMode>,
  )

  // Skip the app's own service worker in mock mode so it can't intercept or cache
  // the mocked API responses.
  if (!USE_MOCK && 'serviceWorker' in navigator) {
    window.addEventListener('load', () => {
      navigator.serviceWorker.register('/sw.js').catch(() => {});
    });
  }
}

bootstrap()
